import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import LessonEditModal from './LessonEditModal';

const DAYS = [
  { id: 1, short: 'Пн', full: 'Понедельник' },
  { id: 2, short: 'Вт', full: 'Вторник' },
  { id: 3, short: 'Ср', full: 'Среда' },
  { id: 4, short: 'Чт', full: 'Четверг' },
  { id: 5, short: 'Пт', full: 'Пятница' },
];
const MONTHS_RU_GEN = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// ВАЖНО: ниже все YYYY-MM-DD считаем в ЛОКАЛЬНОМ времени браузера.
// `Date.toISOString()` всегда отдаёт UTC, и для часовых поясов восточнее
// UTC «00:00 локально» уже превращается в «21:00 предыдущих суток UTC».
// Из-за этого старый `toISOString().slice(0,10)` сдвигал любую дату на
// сутки назад — отсюда баг с шапкой «Пн = суббота» при первом открытии
// расписания (после ручного перелистывания глюк уходил, потому что
// бэк присылал свою корректную дату).
function fmtLocalIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function isoToday() { return fmtLocalIso(new Date()); }
function startOfWeek(iso) {
  const d = new Date(iso + 'T00:00:00');
  const dow = d.getDay() || 7; // 1..7
  if (dow > 1) d.setDate(d.getDate() - (dow - 1));
  return fmtLocalIso(d);
}
function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return fmtLocalIso(d);
}
function fmtWeekRange(start) {
  const a = new Date(start + 'T00:00:00');
  const b = new Date(addDays(start, 6) + 'T00:00:00');
  const fa = `${a.getDate()} ${MONTHS_RU_GEN[a.getMonth()]}`;
  const fb = `${b.getDate()} ${MONTHS_RU_GEN[b.getMonth()]}`;
  return `${fa} — ${fb}`;
}
function dayDate(weekStart, dayIdx /* 1..5 */) {
  return addDays(weekStart, dayIdx - 1);
}

const URL_SCHEMA = { mode: 'class', year_id: '', class_id: '', teacher_id: '', room_id: '', week: '' };

export default function SchedulePage() {
  const navigate = useNavigate();
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('schedule', 'write');
  const canReadJournal = can('journal', 'read');

  const [params, setParams] = useUrlState(URL_SCHEMA);

  // Общие справочники (один раз)
  const [classes, setClasses]   = useState([]);
  const [years, setYears]       = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms]       = useState([]);
  const [refsReady, setRefsReady] = useState(false);

  // Текущая сцена
  const [cls, setCls]         = useState(null);
  const [slots, setSlots]     = useState([]);
  const [lessons, setLessons] = useState([]);
  const [holidays, setHolidays] = useState([]); // [{date, kind, label}]
  const [weekRange, setWeekRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Выбор урока для редактирования
  const [editing, setEditing] = useState(null);

  // Drag-and-drop состояние
  const [dragLessonId, setDragLessonId] = useState(null);
  const [dropTarget, setDropTarget]     = useState(null); // 'D-P'

  // Грузим справочники один раз и атомарно дозаполняем дефолты в URL
  useEffect(() => {
    Promise.all([
      api.get('/api/classes'),
      api.get('/api/subjects'),
      api.get('/api/staff?role=teacher&active=1'),
      api.get('/api/staff?role=administrator&active=1'),
      api.get('/api/rooms').catch(() => ({ rows: [] })),
      api.get('/api/years_study').catch(() => ({ rows: [] })),
    ]).then(([c, s, t, a, r, y]) => {
      const primaries = (c.rows || []).filter((x) => x.stage === 'primary');
      setClasses(primaries);
      const yearsList = (y.rows || []).filter((x) => !x.archive);
      setYears(yearsList);
      setSubjects((s.rows || []).filter((x) => !x.archive));
      const teacherList = [
        ...(t.rows || []),
        ...((a.rows || []).filter((u) => u.canTeach)),
      ];
      teacherList.sort((x, y) => x.name.localeCompare(y.name, 'ru'));
      setTeachers(teacherList);
      setRooms(r.rows || []);

      // Атомарно вычисляем дефолты для week / year_id / class_id и выставляем
      // одним setParams — иначе при первом заходе селект класса успевает
      // отправить запрос с year_id, для которого его нет в списке.
      const today = isoToday();
      const patch = {};
      if (!params.week) {
        patch.week = startOfWeek(today);
      } else {
        // URL мог сохранить «съехавшую» дату (баг ранних версий addDays
        // сдвигал неделю на сутки за каждый клик). Прижимаем к ближайшему
        // понедельнику, чтобы шапка дней и сетка не расходились.
        const fixed = startOfWeek(params.week);
        if (fixed !== params.week) patch.week = fixed;
      }

      let yearId = params.year_id ? Number(params.year_id) : null;
      if (!yearId || !yearsList.some((yr) => yr.id === yearId)) {
        const current = yearsList.find((yr) => yr.dateStart <= today && today <= yr.dateEnd);
        const fallback = current || yearsList[0];
        if (fallback) { yearId = fallback.id; patch.year_id = String(fallback.id); }
      }

      // Если URL приехал с year_id, который НЕ включает текущую неделю,
      // прижимаем неделю к границам года: либо «сегодня», если попадает
      // в год, либо первая неделя года.
      const yr = yearsList.find((y) => y.id === yearId);
      if (yr) {
        const wk = patch.week || params.week || '';
        const wkEnd = wk ? addDays(wk, 6) : '';
        const inRange = wk && wk <= yr.dateEnd && wkEnd >= yr.dateStart;
        if (!inRange) {
          const base = today >= yr.dateStart && today <= yr.dateEnd
            ? today
            : yr.dateStart;
          patch.week = startOfWeek(base);
        }
      }

      if (params.mode === 'class') {
        const inYear = yearId ? primaries.filter((x) => x.yearId === yearId) : primaries;
        const cid = params.class_id ? Number(params.class_id) : 0;
        const stillValid = cid && inYear.some((x) => x.id === cid);
        if (!stillValid && inYear.length) patch.class_id = String(inYear[0].id);
        else if (!stillValid && !inYear.length && params.class_id) patch.class_id = '';
      }

      if (Object.keys(patch).length) setParams(patch);
      setRefsReady(true);
    }).catch(() => { setRefsReady(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Перезагрузка сцены при изменении параметров
  const reload = () => {
    if (!params.week) return;
    const qs = new URLSearchParams();
    qs.set('week', params.week);
    if (params.mode === 'class' && params.class_id) qs.set('class_id', params.class_id);
    else if (params.mode === 'teacher' && params.teacher_id) qs.set('teacher_id', params.teacher_id);
    else if (params.mode === 'room' && params.room_id) qs.set('room_id', params.room_id);
    else { setSlots([]); setLessons([]); setHolidays([]); setCls(null); return; }

    setLoading(true); setError(null);
    api.get('/api/schedule?' + qs.toString())
      .then((res) => {
        setCls(res.class || null);
        setSlots(res.slots || []);
        setLessons(res.lessons || []);
        setHolidays(res.holidays || []);
        setWeekRange(res.week || null);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.week, params.mode, params.class_id, params.teacher_id, params.room_id]);

  // На одном слоте (weekday, position) у класса может лежать несколько
  // уроков с разными `effective_from..effective_to` — например,
  // математика с сентября по декабрь, литература с января. Поэтому
  // храним все варианты массивом и выбираем по дате ячейки.
  const lessonAt = useMemo(() => {
    const map = new Map();
    for (const l of lessons) {
      const key = l.weekday + ':' + l.position;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    return map;
  }, [lessons]);

  // Колонки сетки: Пн–Пт, плюс суббота/воскресенье, если на них выпал
  // перенос выходного (kind=workday + transferFrom): такой день рабочий
  // и живёт по расписанию дня недели даты-источника. Перенос может
  // прийтись и на будний день (вторник «за понедельник») — тогда
  // существующая колонка помечается. Колонки с переносом read-only:
  // уроки в них — это шаблон дня-источника, редактировать их нужно там.
  const cols = useMemo(() => {
    const base = DAYS.map((d) => ({ ...d, transfer: null }));
    if (!params.week) return base;
    const names = {
      1: { short: 'Пн', full: 'Понедельник' }, 2: { short: 'Вт', full: 'Вторник' },
      3: { short: 'Ср', full: 'Среда' },       4: { short: 'Чт', full: 'Четверг' },
      5: { short: 'Пт', full: 'Пятница' },     6: { short: 'Сб', full: 'Суббота' },
      7: { short: 'Вс', full: 'Воскресенье' },
    };
    const weekEnd = addDays(params.week, 6);
    for (const h of holidays) {
      if (h.kind !== 'workday' || !h.transferFrom) continue;
      if (h.date < params.week || h.date > weekEnd) continue;
      const dn    = (new Date(h.date + 'T00:00:00').getDay() || 7);
      const fromN = (new Date(h.transferFrom + 'T00:00:00').getDay() || 7);
      const entry = {
        id: dn,
        ...(names[dn] || { short: '', full: '' }),
        transfer: { from: h.transferFrom, fromN, fromLabel: names[fromN]?.short || '' },
      };
      const i = base.findIndex((x) => x.id === dn);
      if (i >= 0) base[i] = entry; else base.push(entry);
    }
    base.sort((a, b) => a.id - b.id);
    return base;
  }, [holidays, params.week]);

  // Каникулы и праздники недели → date -> {kind, label}. Используем
  // и для скрытия уроков, и для подсветки шапки дня.
  const holidayByDate = useMemo(() => {
    const map = new Map();
    for (const h of holidays) {
      // `workday` — это «отработка» (рабочая суббота и т.п.), занятия
      // в этот день идут как обычно. Скрываем только реально нерабочие.
      if (h.kind === 'holiday' || h.kind === 'vacation' || h.kind === 'preholiday') {
        map.set(h.date, h);
      }
    }
    return map;
  }, [holidays]);

  // Найти урок для конкретной (день, позиция, дата) — учитывает
  // период действия урока и каникулы/праздники. Если дата вне диапазона
  // или попадает на каникулы — null, в ячейке пусто.
  const getLessonAt = (day, position, dateIso) => {
    if (dateIso && holidayByDate.has(dateIso)) {
      const h = holidayByDate.get(dateIso);
      // На каникулы и праздники занятий нет; preholiday (сокращённый
      // предпраздничный день) — формально рабочий, оставляем урок.
      if (h.kind === 'holiday' || h.kind === 'vacation') return null;
    }
    const arr = lessonAt.get(day + ':' + position);
    if (!arr || arr.length === 0) return null;
    if (!dateIso) return arr[0] || null;
    return arr.find((l) => {
      const from = l.effectiveFrom || '0000-01-01';
      const to   = l.effectiveTo   || '9999-12-31';
      return from <= dateIso && dateIso <= to;
    }) || null;
  };

  // Палитра: предметы текущей сцены + количество
  const palette = useMemo(() => {
    const m = new Map();
    for (const l of lessons) {
      const k = l.subjectId;
      if (!m.has(k)) m.set(k, { id: k, name: l.subjectName, color: l.subjectColor, count: 0 });
      m.get(k).count++;
    }
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [lessons]);

  // Клик по пустой ячейке (только в режиме «класс») — открывает форму
  // создания нового урока расписания.
  const onCellClick = (day, position) => {
    if (!canWrite || params.mode !== 'class') return;
    const date = params.week ? dayDate(params.week, day) : null;
    const existing = getLessonAt(day, position, date);
    if (existing) return; // непустую ячейку обрабатывает сама карточка
    setEditing({ weekday: day, position, classId: cls.id, _isNew: true });
  };

  // Клик по карточке урока — переход в электронный журнал на этот класс/предмет/месяц.
  // Месяц берём из реальной даты урока в текущей неделе.
  const onLessonOpen = (l) => {
    if (!canReadJournal) return;
    const date = params.week ? dayDate(params.week, l.weekday) : isoToday();
    const month = date.slice(0, 7);
    const qs = new URLSearchParams({
      year_id:    String(l.yearId),
      class_id:   String(l.classId),
      subject_id: String(l.subjectId),
      month,
    });
    navigate('/journal?' + qs.toString());
  };

  // Клик по шестерёнке на карточке — открывает редактор урока.
  const onLessonEdit = (l) => {
    if (!canWrite) return;
    setEditing(l);
  };

  // === Drag-and-drop (только в режиме «класс») ===
  const onDragStart = (e, l) => {
    if (params.mode !== 'class' || !canWrite) { e.preventDefault(); return; }
    setDragLessonId(l.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e, day, pos) => {
    if (params.mode !== 'class' || dragLessonId === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(day + '-' + pos);
  };
  const onDragLeave = () => setDropTarget(null);
  const onDrop = async (e, day, pos) => {
    e.preventDefault();
    setDropTarget(null);
    if (params.mode !== 'class' || dragLessonId === null) return;
    const l = lessons.find((x) => x.id === dragLessonId);
    setDragLessonId(null);
    if (!l) return;
    if (l.weekday === day && l.position === pos) return;

    // Локально оптимистично обновим — учитываем дату целевой ячейки,
    // если на слоте лежат уроки разных периодов.
    const dropDate = params.week ? dayDate(params.week, day) : null;
    const target = getLessonAt(day, pos, dropDate);
    if (target) {
      // swap — если в целевой ячейке уже урок, поменяем местами
      try {
        await api.put('/api/schedule/' + target.id,    { ...toPayload(target), weekday: l.weekday, position: l.position });
        await api.put('/api/schedule/' + l.id,         { ...toPayload(l),      weekday: day,        position: pos });
        reload();
      } catch (err) {
        alert(err instanceof ApiError ? err.message : 'Не удалось переместить');
        reload();
      }
      return;
    }
    try {
      await api.put('/api/schedule/' + l.id, { ...toPayload(l), weekday: day, position: pos });
      reload();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'conflict') {
        // Конфликт — открываем модалку с warningom
        setEditing({ ...l, weekday: day, position: pos, _moveConflict: err.payload?.error?.conflicts || [] });
      } else {
        alert(err instanceof ApiError ? err.message : 'Не удалось переместить');
        reload();
      }
    }
  };

  const onSaved = (l) => {
    setLessons((prev) => {
      const i = prev.findIndex((x) => x.id === l.id);
      if (i >= 0) { const n = prev.slice(); n[i] = l; return n; }
      return [...prev, l];
    });
    setEditing(null);
  };
  const onDeleted = (id) => {
    setLessons((prev) => prev.filter((x) => x.id !== id));
    setEditing(null);
  };

  const goPrev   = () => setParams({ week: addDays(params.week || isoToday(), -7) });
  const goNext   = () => setParams({ week: addDays(params.week || isoToday(),  7) });
  const goToday  = () => setParams({ week: startOfWeek(isoToday()) });

  const sceneEmpty =
    (params.mode === 'class'   && !params.class_id) ||
    (params.mode === 'teacher' && !params.teacher_id) ||
    (params.mode === 'room'    && !params.room_id);

  const classesInYear = useMemo(() => {
    if (!params.year_id) return classes;
    const yid = Number(params.year_id);
    return classes.filter((c) => c.yearId === yid);
  }, [classes, params.year_id]);

  const onYearChange = (e) => {
    const yid = e.target.value;
    const yidNum = Number(yid);
    const inYear = classes.filter((c) => c.yearId === yidNum);
    const patch = { year_id: yid };
    if (params.mode === 'class') {
      const cid = params.class_id ? Number(params.class_id) : 0;
      if (!cid || !inYear.some((c) => c.id === cid)) {
        patch.class_id = inYear.length ? String(inYear[0].id) : '';
      }
    }
    // Если текущая неделя не пересекается с выбранным годом —
    // прыгаем на первую неделю года (или на сегодняшнюю, если она
    // уже внутри года). Без этого пользователь видит «пустое»
    // расписание (или съехавшие даты, если в URL осталась битая
    // дата от прежнего бага в addDays).
    const yr = years.find((y) => y.id === yidNum);
    if (yr) {
      const today = isoToday();
      const weekIso = params.week || '';
      const weekEnd = weekIso ? addDays(weekIso, 6) : '';
      const inRange = weekIso
        && weekIso <= yr.dateEnd
        && weekEnd >= yr.dateStart;
      if (!inRange) {
        const base = today >= yr.dateStart && today <= yr.dateEnd
          ? today
          : yr.dateStart;
        patch.week = startOfWeek(base);
      }
    }
    setParams(patch);
  };

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Расписание']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="sch-toolbar">
        {/* Режим */}
        <div className="sch-segmented">
          {[
            { v: 'class',   l: 'Класс' },
            { v: 'teacher', l: 'Учитель' },
            { v: 'room',    l: 'Кабинет' },
          ].map((m) => (
            <button
              key={m.v}
              className={params.mode === m.v ? 'is-active' : ''}
              onClick={() => setParams({ mode: m.v })}
            >{m.l}</button>
          ))}
        </div>

        {/* Селектор сцены */}
        {params.mode === 'class' && (
          <>
            <select
              value={params.year_id || ''}
              onChange={onYearChange}
              className="sch-toolbar__select"
            >
              <option value="">— учебный год —</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>{y.name}</option>
              ))}
            </select>
            <select
              value={params.class_id || ''}
              onChange={(e) => setParams({ class_id: e.target.value })}
              className="sch-toolbar__select"
              disabled={!params.year_id || classesInYear.length === 0}
            >
              <option value="">— выберите класс —</option>
              {classesInYear.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code}{c.name && c.name !== c.code ? ' · ' + c.name : ''}
                </option>
              ))}
            </select>
          </>
        )}
        {params.mode === 'teacher' && (
          <select
            value={params.teacher_id || ''}
            onChange={(e) => setParams({ teacher_id: e.target.value })}
            className="sch-toolbar__select"
          >
            <option value="">— выберите учителя —</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.role === 'administrator' ? ' (админ)' : ''}</option>
            ))}
          </select>
        )}
        {params.mode === 'room' && (
          <select
            value={params.room_id || ''}
            onChange={(e) => setParams({ room_id: e.target.value })}
            className="sch-toolbar__select"
          >
            <option value="">— выберите кабинет —</option>
            {rooms.filter((r) => !r.archive).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        )}

        <div style={{ flex: 1 }} />

        {/* Переключатель недели */}
        <div className="sch-weekswitch">
          <button onClick={goPrev} title="Предыдущая неделя"><Icon name="chevron" size={11} /></button>
          <span className="sch-weekswitch__label">
            {params.week ? fmtWeekRange(params.week) : ''}
          </span>
          <button onClick={goNext} title="Следующая неделя"><Icon name="chevron" size={11} /></button>
          <button onClick={goToday} className="sch-weekswitch__today">Сегодня</button>
        </div>
      </div>

      <div className="sch-body">
        {/* Палитра: предметы сцены с количеством */}
        <aside className="sch-pal">
          <div className="sch-pal__title">Предметы недели</div>
          <div className="sch-pal__hint">{lessons.length} занятий за {weekRange ? fmtWeekRange(weekRange.start) : 'неделю'}</div>
          <div className="sch-pal__list">
            {palette.length === 0
              ? <div className="sch-pal__empty">Нет занятий</div>
              : palette.map((p) => (
                  <div key={p.id} className="sch-pal__item">
                    <span className="sch-pal__sw" style={{ background: p.color || '#888' }} />
                    <span className="sch-pal__name">{p.name}</span>
                    <span className="sch-pal__count">{p.count}</span>
                  </div>
                ))
            }
          </div>
        </aside>

        {/* Сетка */}
        <div className="sch-content">
          {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
          {!refsReady ? (
            <div className="ref-empty">Загрузка…</div>
          ) : sceneEmpty ? (
            <div className="ref-empty">Выберите {params.mode === 'class' ? 'класс' : params.mode === 'teacher' ? 'учителя' : 'кабинет'}.</div>
          ) : loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : slots.length === 0 ? (
            <div className="ref-empty">
              {params.mode === 'class'
                ? 'Для этого класса не задано слотов времени уроков.'
                : 'Слоты времени не настроены.'}
            </div>
          ) : (
            <div className="sch-grid" style={{ '--days': cols.length }}>
              <div className="sch-grid__corner">Время</div>
              {cols.map((d) => {
                const date = params.week ? dayDate(params.week, d.id) : null;
                const h = date ? holidayByDate.get(date) : null;
                const headClass = 'sch-grid__dayhead' + (h ? ' is-holiday' : '');
                return (
                  <div key={d.id} className={headClass}>
                    <div className="sch-grid__dayhead-name">{d.full}</div>
                    <div className="sch-grid__dayhead-short">
                      {d.short}{date ? ' · ' + date.slice(8) + '.' + date.slice(5,7) : ''}
                    </div>
                    {d.transfer && (
                      <div className="sch-grid__dayhead-holiday"
                           title={'Перенос выходного: день рабочий, занятия по расписанию ' + d.transfer.fromLabel
                             + '. Уроки редактируются в колонке дня-источника.'}>
                        по расписанию {d.transfer.fromLabel}
                      </div>
                    )}
                    {h && (
                      <div className="sch-grid__dayhead-holiday" title={h.label || ''}>
                        {h.kind === 'vacation' ? 'Каникулы'
                          : h.kind === 'holiday' ? 'Выходной'
                          : 'Сокр. день'}
                      </div>
                    )}
                  </div>
                );
              })}

              {slots.map((s) => (
                <React.Fragment key={s.position}>
                  <div className="sch-grid__time">
                    <div className="sch-grid__time-n">{s.position} урок</div>
                    <div className="sch-grid__time-from mono">{s.timeStart}</div>
                    <div className="sch-grid__time-to mono">до {s.timeEnd}</div>
                  </div>
                  {cols.map((d) => {
                    const cellDate = params.week ? dayDate(params.week, d.id) : null;
                    const effDay = d.transfer ? d.transfer.fromN : d.id;
                    const l = getLessonAt(effDay, s.position, cellDate);
                    const cellKey = d.id + '-' + s.position;
                    const editable = canWrite && params.mode === 'class' && !d.transfer;
                    return (
                      <div
                        key={cellKey}
                        className={
                          'sch-cell' + (l ? '' : ' is-empty') + (editable ? ' is-editable' : '') +
                          (dropTarget === cellKey ? ' is-droptarget' : '')
                        }
                        onClick={editable ? () => onCellClick(d.id, s.position) : undefined}
                        onDragOver={editable ? (e) => onDragOver(e, d.id, s.position) : undefined}
                        onDragLeave={editable ? onDragLeave : undefined}
                        onDrop={editable ? (e) => onDrop(e, d.id, s.position) : undefined}
                      >
                        {l && (
                          <div
                            className={'sch-lesson' + (canReadJournal ? ' is-clickable' : '')}
                            style={{ borderLeftColor: l.subjectColor || '#888' }}
                            draggable={editable}
                            onDragStart={(e) => onDragStart(e, l)}
                            onDragEnd={() => { setDragLessonId(null); setDropTarget(null); }}
                            onClick={(e) => { e.stopPropagation(); onLessonOpen(l); }}
                            title={canReadJournal ? 'Перейти в электронный дневник' : undefined}
                          >
                            <div className="sch-lesson__top">
                              <span className="sch-lesson__time mono">{s.timeStart}–{s.timeEnd}</span>
                              {canWrite && !d.transfer && (
                                <button
                                  type="button"
                                  className="sch-lesson__edit"
                                  onClick={(e) => { e.stopPropagation(); onLessonEdit(l); }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  draggable={false}
                                  title="Редактировать урок расписания"
                                >
                                  <Icon name="gear" size={11} />
                                </button>
                              )}
                            </div>
                            <div className="sch-lesson__name">{l.subjectName}</div>
                            {params.mode === 'class' && l.teacherName && (
                              <div className="sch-lesson__meta">{l.teacherName}</div>
                            )}
                            {params.mode !== 'class' && (
                              <div className="sch-lesson__meta">
                                <b>{l.classCode}</b>{l.teacherName ? ' · ' + l.teacherName : ''}
                              </div>
                            )}
                            <div className="sch-lesson__meta">
                              {l.roomName && <>каб. {l.roomName}</>}
                              {l.note && <span className="sch-lesson__note"> · {l.note}</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <LessonEditModal
          editing={editing}
          classRow={cls || (params.mode === 'class' ? null : null)}
          subjects={subjects}
          teachers={teachers}
          rooms={rooms}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </>
  );
}

/** Преобразование public-объекта урока в payload для PUT (без вычисляемых полей). */
function toPayload(l) {
  return {
    classId: l.classId,
    weekday: l.weekday,
    position: l.position,
    subjectId: l.subjectId,
    teacherId: l.teacherId,
    roomId: l.roomId,
    note: l.note,
    effectiveFrom: l.effectiveFrom,
    effectiveTo: l.effectiveTo,
  };
}
